import { GridColDef } from '@mui/x-data-grid-pro';
import { isValidElement, ReactNode } from 'react';

const MIN_COLUMN_WIDTH = 100;
const MAX_COLUMN_WIDTH = 600;
const CELL_HORIZONTAL_SPACE = 24;
const HEADER_HORIZONTAL_SPACE = 52;
const CELL_FONT = '12px Nunito, Helvetica, Arial, sans-serif';
const HEADER_FONT = '600 12px Nunito, Helvetica, Arial, sans-serif';
const MAX_MEASURED_CHARACTERS = 500;

type GridRow = Record<string, unknown>;
type TextWidthMeasurer = (text: string, font: string) => number;

interface AutoColumnWidthOptions {
  measureText?: TextWidthMeasurer;
  getCellDisplayValue?: (row: GridRow, rowIndex: number) => unknown;
  additionalCellWidth?: number;
}

let textMeasurementContext: CanvasRenderingContext2D | null | undefined;

function getTextMeasurementContext() {
  if (textMeasurementContext !== undefined) return textMeasurementContext;
  if (typeof document === 'undefined') return null;

  const canvas = document.createElement('canvas');
  textMeasurementContext = canvas.getContext('2d');
  return textMeasurementContext;
}

function estimateTextWidth(text: string) {
  return Array.from(text).reduce((width, character) => {
    if (/\s/.test(character)) return width + 3;
    if (/[ilI1.,'`|]/.test(character)) return width + 4;
    if (/[MW@#%&]/.test(character)) return width + 10;
    if (character.charCodeAt(0) > 255) return width + 12;
    return width + 7;
  }, 0);
}

function measureTextWidth(text: string, font: string) {
  const context = getTextMeasurementContext();
  if (!context) return estimateTextWidth(text);

  context.font = font;
  return context.measureText(text).width;
}

export function getGridCellDisplayText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') return String(value);
  if (value instanceof Date) return value.toLocaleString();
  if (Array.isArray(value)) return value.map(getGridCellDisplayText).filter(Boolean).join(', ');

  if (typeof value === 'object') {
    const displayValue = value as Record<string, unknown>;
    const preferredValue = displayValue.label ?? displayValue.member ?? displayValue.name ?? displayValue.value;
    if (preferredValue !== null && preferredValue !== undefined && preferredValue !== value) {
      return getGridCellDisplayText(preferredValue);
    }

    const customString = String(value);
    if (customString !== '[object Object]') return customString;

    try {
      return JSON.stringify(value) || '';
    } catch {
      return '';
    }
  }

  return String(value);
}

function getReactNodeDisplayText(node: ReactNode, nested: boolean = false): string | undefined {
  if (node === null || node === undefined) return nested ? '' : undefined;
  if (typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);

  if (Array.isArray(node)) {
    const childText = node
      .map((child) => getReactNodeDisplayText(child, true))
      .filter((value) => value !== undefined) as string[];
    return childText.length ? childText.join('') : undefined;
  }

  if (isValidElement(node)) {
    if (!Object.prototype.hasOwnProperty.call(node.props, 'children')) {
      return typeof node.type === 'string' ? '' : undefined;
    }
    return getReactNodeDisplayText(node.props.children, true);
  }

  return undefined;
}

function getFormattedCellValue(column: GridColDef, row: GridRow, rowIndex: number) {
  const rawValue = row[column.field];
  const params = {
    id: row.id ?? rowIndex,
    field: column.field,
    value: rawValue,
    row,
    colDef: column,
    rowNode: undefined,
    cellMode: 'view',
    hasFocus: false,
    tabIndex: -1,
    getValue: (_id: unknown, field: string) => row[field],
    api: undefined,
  };

  let value = rawValue;

  try {
    if (column.valueGetter) {
      const resolvedValue = column.valueGetter(params as never);
      if (resolvedValue !== undefined) value = resolvedValue;
    }
  } catch {
    value = rawValue;
  }

  let formattedValue = value;
  try {
    if (column.valueFormatter) {
      const resolvedFormattedValue = column.valueFormatter({ ...params, value } as never);
      if (resolvedFormattedValue !== undefined) formattedValue = resolvedFormattedValue;
    }
  } catch {
    formattedValue = value;
  }

  if (column.renderCell) {
    try {
      const renderedValue = column.renderCell({ ...params, value, formattedValue } as never);
      const renderedText = getReactNodeDisplayText(renderedValue);
      if (renderedText !== undefined) return renderedText;
    } catch {
      // Fall back to the getter/formatter value when a renderer needs the mounted grid API.
    }
  }

  return getGridCellDisplayText(formattedValue);
}

export function getAutoColumnWidth(
  column: GridColDef,
  rows: readonly GridRow[],
  options: AutoColumnWidthOptions = {}
) {
  const {
    measureText = measureTextWidth,
    getCellDisplayValue,
    additionalCellWidth = 0,
  } = options;
  const headerText = column.headerName || column.field;
  const headerWidth = measureText(headerText.slice(0, MAX_MEASURED_CHARACTERS), HEADER_FONT) + HEADER_HORIZONTAL_SPACE;
  const minWidth = Math.max(MIN_COLUMN_WIDTH, column.minWidth || 0);
  const maxWidth = Math.max(minWidth, Math.min(MAX_COLUMN_WIDTH, column.maxWidth || MAX_COLUMN_WIDTH));
  let contentWidth = 0;

  for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex];
    const displayText = getCellDisplayValue
      ? getGridCellDisplayText(getCellDisplayValue(row, rowIndex))
      : getFormattedCellValue(column, row, rowIndex);
    const measuredWidth =
      measureText(displayText.slice(0, MAX_MEASURED_CHARACTERS), CELL_FONT) +
      CELL_HORIZONTAL_SPACE +
      additionalCellWidth;
    contentWidth = Math.max(contentWidth, measuredWidth);
    if (contentWidth >= maxWidth) break;
  }

  return Math.ceil(Math.min(maxWidth, Math.max(minWidth, headerWidth, contentWidth)));
}

export function getAutoSizedColumn(
  column: GridColDef,
  rows: readonly GridRow[],
  options?: AutoColumnWidthOptions
): GridColDef {
  return {
    ...column,
    flex: undefined,
    width: getAutoColumnWidth(column, rows, options),
  };
}
